// Passive replay of a finished computer game. Selecting a recent game restores
// the board and move navigation without starting Stockfish; analysis begins only
// when the member presses the explicit Analyze button.

import { Chessground } from "chessground";
import { MoveReplay } from "../game/replay.ts";
import type { Api as CgApi } from "chessground/api";

import { checkColor, parsePosition, uciSquares } from "../game/board.ts";
import { getBotGame, type BotGameRecord } from "../game/botHistory.ts";
import {
  buildReview,
  capturedFor,
  isTypingTarget,
  navControls,
  navKey,
  paintCaptured,
  scrollIntoListView,
  type GameNode,
  type NavAction,
} from "../game/review.ts";
import type { Color } from "../game/types.ts";
import { esc, h } from "../dom.ts";
import { t } from "../i18n/index.ts";
import { navigate, type Params } from "../router.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { boardSettings } from "../ui/preferences.ts";
import { mountBotAnalysis } from "./analysis.ts";
import { buildPgn, metaFromBotGame, pgnFilename } from "../game/pgn.ts";
import { downloadText } from "../ui/clipboard.ts";

let active: BotReviewCtrl | null = null;

export function botGameScreen(params: Params): HTMLElement {
  active?.destroy();
  active = null;

  const id = params.id ?? "";
  const game = getBotGame(id);
  if (!game) return notice(t("analysis.botGameGone"));
  return botGameReview(game);
}

export function botGameReview(game: BotGameRecord, saved = true): HTMLElement {
  const nodes = buildReview(game.moves).nodes;
  const el = buildLayout(game);
  el.querySelector(".side")!.append(boardSettings("replay"));
  if (!saved) {
    const note = document.createElement("p");
    note.className = "fine";
    note.textContent = t("computer.saveFailed");
    el.querySelector(".result")!.append(note);
  }
  requestAnimationFrame(() => {
    if (!el.isConnected) return;
    const ctrl = new BotReviewCtrl(el, game, nodes, saved);
    active = ctrl;
    onDisconnect(el, () => {
      ctrl.destroy();
      if (active === ctrl) active = null;
    });
  });
  return el;
}

class BotReviewCtrl {
  private cg: CgApi;
  private replay: MoveReplay;
  private current: number;
  private onKey: (event: KeyboardEvent) => void;
  private destroyed = false;

  constructor(
    private root: HTMLElement,
    private game: BotGameRecord,
    private nodes: GameNode[],
    private saved: boolean,
  ) {
    this.current = Math.max(0, nodes.length - 1);
    const node = nodes[this.current];
    const pos = parsePosition(node.fen);
    this.cg = Chessground(root.querySelector<HTMLElement>(".cg-board-wrap")!, {
      fen: node.fen,
      orientation: game.humanColor,
      turnColor: node.turn,
      lastMove: node.uci ? uciSquares(node.uci) : undefined,
      check: pos ? checkColor(pos) : undefined,
      viewOnly: true,
      movable: { free: false, color: undefined, dests: new Map() },
      premovable: { enabled: false },
      draggable: { enabled: false },
      highlight: { lastMove: true, check: true },
      animation: { enabled: true, duration: 160 },
    });

    this.replay = new MoveReplay(root,
      () => ({ current: this.current, last: this.nodes.length - 1 }),
      ply => this.goTo(ply, false));
    this.renderMoves();
    this.wireNav();
    this.onKey = (event) => this.handleKey(event);
    document.addEventListener("keydown", this.onKey);
    this.show();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    document.removeEventListener("keydown", this.onKey);
    this.replay.destroy();
    this.cg.destroy();
  }

  private show(): void {
    this.current = Math.max(0, Math.min(this.nodes.length - 1, this.current));
    const node = this.nodes[this.current];
    const pos = parsePosition(node.fen);
    this.cg.set({
      fen: node.fen,
      turnColor: node.turn,
      lastMove: node.uci ? uciSquares(node.uci) : undefined,
      check: pos ? checkColor(pos) : undefined,
      viewOnly: true,
      movable: { color: undefined, dests: new Map() },
    });

    const captured = capturedFor(node.fen);
    const white = this.root.querySelector<HTMLElement>('[data-captured="white"]');
    const black = this.root.querySelector<HTMLElement>('[data-captured="black"]');
    if (white) paintCaptured(white, "white", captured);
    if (black) paintCaptured(black, "black", captured);

    const list = this.root.querySelector<HTMLElement>(".movelist")!;
    list.querySelectorAll(".san.cur").forEach((el) => el.classList.remove("cur"));
    const current = list.querySelector<HTMLElement>(`[data-ply="${this.current}"]`);
    if (current) {
      current.classList.add("cur");
      scrollIntoListView(list, current);
    }
    this.updateNav();
    this.replay.update();
  }

  private goTo(ply: number, manual = true): void {
    if (manual) this.replay.pause();
    this.current = ply;
    this.show();
  }

  private go(action: NavAction): void {
    const last = this.nodes.length - 1;
    this.goTo(
      action === "first"
        ? 0
        : action === "last"
          ? last
          : action === "prev"
            ? this.current - 1
            : this.current + 1);
  }

  private wireNav(): void {
    this.root.querySelector<HTMLElement>(".nav")!.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-nav]");
      const action = button?.dataset.nav as NavAction | undefined;
      if (action) this.go(action);
    });
    this.root.querySelector<HTMLElement>(".movelist")!.addEventListener("click", (event) => {
      const move = (event.target as HTMLElement).closest<HTMLElement>("[data-ply]");
      if (!move) return;
      this.goTo(Number(move.dataset.ply));
    });
    this.root.querySelector<HTMLButtonElement>('[data-act="analyze"]')!.onclick = () => {
      if (this.saved) navigate(`/analysis/bot/${encodeURIComponent(this.game.id)}`);
      else this.root.replaceWith(mountBotAnalysis(this.game));
    };
    this.root.querySelector<HTMLButtonElement>('[data-act="download"]')!.onclick = () => {
      const meta = metaFromBotGame(this.game);
      downloadText(pgnFilename(meta), buildPgn(buildReview(this.game.moves), meta));
    };
    this.root.querySelector<HTMLButtonElement>('[data-act="new"]')!.onclick = () => navigate("/play/computer");
  }

  private handleKey(event: KeyboardEvent): void {
    if (isTypingTarget(event.target)) return;
    const action = navKey(event);
    if (!action) return;
    event.preventDefault();
    this.go(action);
  }

  private updateNav(): void {
    const last = this.nodes.length - 1;
    for (const [action, disabled] of [
      ["first", this.current <= 0],
      ["prev", this.current <= 0],
      ["next", this.current >= last],
      ["last", this.current >= last],
    ] as const) {
      const button = this.root.querySelector<HTMLButtonElement>(`.nav [data-nav="${action}"]`);
      if (button) button.disabled = disabled;
    }
  }

  private renderMoves(): void {
    const list = this.root.querySelector<HTMLElement>(".movelist")!;
    list.replaceChildren();
    for (let ply = 1; ply < this.nodes.length; ply += 2) {
      const row = document.createElement("div");
      row.className = "move-row";
      row.innerHTML = `<span class="mn">${(ply + 1) / 2}.</span>`;
      row.append(this.moveSpan(ply));
      row.append(ply + 1 < this.nodes.length ? this.moveSpan(ply + 1) : blankSpan());
      list.append(row);
    }
  }

  private moveSpan(ply: number): HTMLElement {
    const move = document.createElement("span");
    move.className = "san";
    move.dataset.ply = String(ply);
    move.textContent = this.nodes[ply].san ?? "";
    return move;
  }
}

function buildLayout(game: BotGameRecord): HTMLElement {
  const topColor: Color = game.humanColor === "white" ? "black" : "white";
  const bottomColor = game.humanColor;
  const topName = topColor === game.humanColor ? t("common.you") : t("chess.computer");
  const bottomName = bottomColor === game.humanColor ? t("common.you") : t("chess.computer");
  const outcome =
    game.winner === null ? "draw" : game.winner === game.humanColor ? "won" : "lost";
  const outcomeText =
    outcome === "won" ? t("result.youWon") : outcome === "lost" ? t("result.youLost") : t("result.drawWord");

  const el = h(`
    <main class="game bot-review">
      <div class="board-col">
        <div class="cg-board-wrap"></div>
      </div>
      <aside class="side">
        <div class="side-top">
          <span class="conn ok">${esc(t("chess.computer"))} · ${esc(t("levels.levelN", { n: game.level }))}</span>
          <span>${esc(game.tc.includes("+") ? game.tc : t("clock.untimed"))}</span>
          <a class="btn ghost sm" data-link href="/my-games">${t("nav.myGames")}</a>
        </div>

        <div class="player">
          <div class="pmeta">
            <span class="pname">${esc(topName)}</span>
            <span class="captured" data-captured="${topColor}"></span>
          </div>
        </div>

        <div class="movelist analysis-moves"></div>
        <div class="nav-slot"></div>

        <div class="player">
          <div class="pmeta">
            <span class="pname">${esc(bottomName)}</span>
            <span class="captured" data-captured="${bottomColor}"></span>
          </div>
        </div>

        <div class="result">
          <div class="result-head ${outcome}">
            <span class="result-score">${score(game.winner)}</span>
            <span class="result-word">${esc(outcomeText)}</span>
          </div>
          <p class="result-reason">${esc(termination(game.status))}</p>
          <div class="result-actions">
            <button class="btn primary" data-act="analyze" type="button">${t("game.analyze")}</button>
            <button class="btn ghost" data-act="download" type="button">${t("analysis.downloadPgn")}</button>
            <button class="btn ghost" data-act="new" type="button">${t("computer.newGame")}</button>
            <a class="btn ghost" data-link href="/">${t("common.home")}</a>
          </div>
        </div>
      </aside>
    </main>
  `);
  el.querySelector<HTMLElement>(".nav-slot")!.append(navControls());
  return el;
}

function score(winner: Color | null): string {
  return winner === "white" ? "1–0" : winner === "black" ? "0–1" : "½–½";
}

function termination(status: string): string {
  const labels: Record<string, string> = {
    mate: t("term.checkmate"),
    resign: t("term.resignation"),
    timeout: t("term.timeout"),
    stalemate: t("term.stalemate"),
    insufficient: t("term.insufficient"),
    threefold: t("term.repetition"),
    fifty: t("term.fiftyMove"),
    draw: t("term.draw"),
  };
  return labels[status] ?? status;
}

function blankSpan(): HTMLElement {
  const span = document.createElement("span");
  span.className = "san";
  return span;
}

function notice(body: string): HTMLElement {
  return h(`
    <main class="card">
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="msg bad">${esc(body)}</p>
      <p><a class="btn ghost" data-link href="/">${t("common.backHome")}</a></p>
    </main>
  `);
}
